import { ThemeProvider } from '@mui/material';
import { DEFAULT_CURRENCY } from '@weft/types';
import { PLAN_CREATION_REASONS } from 'apps/app/src/contexts/create-plan-context/create-plan-context-types/plan-create-reason';
import React from 'react';
import { Helmet } from 'react-helmet';
import { muiDefaultTheme } from '../../../assets/themes/default-theme';
import { vyTheme } from '../../../assets/themes/vy-theme';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import type { PlanCreationStep } from '../../../contexts/create-plan-context/create-plan-context-types/plan-creation-step';
import { PLAN_CREATION_STEPS } from '../../../contexts/create-plan-context/create-plan-context-types/plan-creation-step';
import { CreatePlanProvider } from '../../../contexts/create-plan-context/CreatePlanProvider/CreatePlanProvider';
import { FormErrorNotificationProvider } from '../../../contexts/form-error-notification-context/FormErrorNotificationProvider';
import { useMeters } from '../../../hooks/requests/use-meters';
import { useDialog } from '../../../hooks/use-dialog';
import { CreatePlanErrorNotification } from './create-plan-page-components/CreatePlanErrorNotification/CreatePlanErrorNotification';
import { CreatePlanPageHeader } from './create-plan-page-components/CreatePlanPageHeader';
import { CreatePlanPageStepper } from './create-plan-page-components/CreatePlanPageStepper/CreatePlanPageStepper';
import { CreatePlanPageStepperNavigator } from './create-plan-page-components/CreatePlanPageStepperNavigator';
import { CreatePlanPageTabs } from './create-plan-page-components/CreatePlanPageTabs/CreatePlanPageTabs';
import { NewCreatePlanPageContent } from './create-plan-page-components/NewCreatePlanPageContent/NewCreatePlanPageContent';
import { PlanSettings } from './create-plan-page-components/PlanSettings';
import { SaveAndAssignAsContractDialogForm } from './create-plan-page-components/SaveAndAssignAsContractDialogForm/SaveAndAssignAsContractDialogForm';
import { PLAN_CREATION_STEPS_CTA_LABELS } from './create-plan-page-consts/create-plan-default-values';
import { FormType } from './FormType';
import { withPlanForm } from './withPlanForm';

const PLAN_CREATION_STEPS_CONTENT: Record<
PlanCreationStep,
React.ReactElement
> = {
  [PLAN_CREATION_STEPS.pricingAndProducts]: <CreatePlanPageTabs />,
  [PLAN_CREATION_STEPS.settings]: (
    <PlanSettings />
  ),
};
const CreatePlanPage: React.FC = () => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  useMeters();

  return (
    <>
      <Helmet>
        <title>Plans</title>
      </Helmet>

      <CreatePlanProvider
        planCreationReason={PLAN_CREATION_REASONS.NewPlan}
        defaultPlanCreationStep={PLAN_CREATION_STEPS.pricingAndProducts}
        onFinalStep={() => handleOpen()}
      >
        <FormErrorNotificationProvider>
          <CreatePlanPageHeader/>

          <ThemeProvider theme={vyTheme}>
            <CreatePlanPageStepper currency={DEFAULT_CURRENCY}/>
          </ThemeProvider>

          <ThemeProvider theme={muiDefaultTheme}>
            <NewCreatePlanPageContent stepsContent={PLAN_CREATION_STEPS_CONTENT}/>
          </ThemeProvider>

          <CreatePlanErrorNotification/>

          <ThemeProvider theme={vyTheme}>
            <CreatePlanPageStepperNavigator
              stepsLabels={PLAN_CREATION_STEPS_CTA_LABELS}
              formClearable={true}
            />

            <SaveAndAssignAsContractDialogForm
              open={isDialogOpen}
              onClose={() => handleClose()}
            />
          </ThemeProvider>
        </FormErrorNotificationProvider>
      </CreatePlanProvider>
    </>
  );
};

const CreatePlanPageWithSuspenseBoundary = withSuspenseBoundary(withGlobalSearchParams(withPlanForm(CreatePlanPage, FormType.CreatePlanForm)));
export { CreatePlanPageWithSuspenseBoundary as CreatePlanPage };